import React, { useState, useCallback } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiSave, FiTrash2 } from "react-icons/fi";
import { showToast } from "utils/helpers";
import { useShortlistPresets, useCreatePreset, useUpdatePreset, useDeletePreset } from "../../hooks/useShortlistPresets";
import SavePresetDialog from "../SavePresetDialog";
import type { ShortlistPreset } from "../../api/shortlist-report-api";
import type { SectionVisibility, FieldVisibility, CustomisationState } from "../../hooks/useShortlistReportState";

interface PresetPanelProps {
	state: CustomisationState;
	onLoadPreset: (preset: Partial<CustomisationState> & { presetId: number; presetName: string }) => void;
	onSetFields: (fields: FieldVisibility) => void;
	onSetSections: (sections: SectionVisibility) => void;
}

/** Build a settings_snapshot from current customisation state */
const buildSnapshot = (state: CustomisationState): Record<string, unknown> => ({
	sections: state.sections,
	fields: state.fields,
	sectorIds: state.sectorIds,
	projectId: state.projectId,
	projectDescription: state.projectDescription,
	includeProjectPage: state.includeProjectPage,
	coverProjectName: state.coverProjectName || undefined,
	coverSupplierRole: state.coverSupplierRole || undefined,
	coverPresentedTo: state.coverPresentedTo || undefined,
	coverPresentedToRole: state.coverPresentedToRole || undefined,
	coverShowLogo: state.coverShowLogo,
	coverCreatedByRole: state.coverCreatedByRole || undefined,
	coverPageMode: state.coverPageMode !== "global" ? state.coverPageMode : undefined,
});

/** Apply a preset's snapshot to the customisation state, skipping missing keys gracefully */
const applySnapshot = (
	snapshot: Record<string, unknown>,
	preset: ShortlistPreset
): Partial<CustomisationState> & { presetId: number; presetName: string } => {
	const result: Partial<CustomisationState> & { presetId: number; presetName: string } = {
		presetId: preset.id,
		presetName: preset.name,
	};

	if (snapshot.sections && typeof snapshot.sections === "object") {
		result.sections = snapshot.sections as SectionVisibility;
	}
	if (snapshot.fields && typeof snapshot.fields === "object") {
		result.fields = snapshot.fields as FieldVisibility;
	}
	if (preset.include_additional_text) {
		result.additionalText = preset.additional_text ?? "";
		result.includeAdditionalText = true;
	}
	if (typeof snapshot.coverProjectName === "string") {
		result.coverProjectName = snapshot.coverProjectName;
	}
	if (typeof snapshot.coverSupplierRole === "string") {
		result.coverSupplierRole = snapshot.coverSupplierRole;
	}
	if (typeof snapshot.coverPresentedTo === "string") {
		result.coverPresentedTo = snapshot.coverPresentedTo;
	}
	if (typeof snapshot.coverPresentedToRole === "string") {
		result.coverPresentedToRole = snapshot.coverPresentedToRole;
	}
	if (typeof snapshot.coverShowLogo === "boolean" || snapshot.coverShowLogo === null) {
		result.coverShowLogo = snapshot.coverShowLogo as boolean | null;
	}
	if (typeof snapshot.coverCreatedByRole === "string") {
		result.coverCreatedByRole = snapshot.coverCreatedByRole;
	}
	if (typeof snapshot.coverPageMode === "string") {
		result.coverPageMode = snapshot.coverPageMode as CustomisationState["coverPageMode"];
	}

	return result;
};

const PresetPanel: React.FC<PresetPanelProps> = ({ state, onLoadPreset, onSetSections, onSetFields }) => {
	const { data: presets, isLoading, isError } = useShortlistPresets();
	const createMutation = useCreatePreset();
	const updateMutation = useUpdatePreset();
	const deleteMutation = useDeletePreset();

	const [showSaveDialog, setShowSaveDialog] = useState(false);
	const [saveMode, setSaveMode] = useState<"create" | "update">("create");
	const [confirmDeleteUuid, setConfirmDeleteUuid] = useState<string | null>(null);

	const activePreset = presets?.find((p) => p.id === state.presetId) ?? null;

	const handleLoadPreset = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			const uuid = e.target.value;
			if (!uuid || !presets) return;

			const preset = presets.find((p) => p.uuid === uuid);
			if (!preset) return;

			const snapshot = preset.settings_snapshot ?? {};
			const applied = applySnapshot(snapshot, preset);

			// Warn about stale data
			const snapshotSections = snapshot.sections as Record<string, boolean> | undefined;
			if (snapshotSections) {
				const currentKeys = Object.keys(state.sections);
				const presetKeys = Object.keys(snapshotSections);
				const missing = presetKeys.filter((k) => !currentKeys.includes(k));
				if (missing.length > 0) {
					showToast(
						`Preset skipped ${missing.length} section(s) no longer available`,
						"Warning",
						true
					);
				}
			}

			onLoadPreset(applied);
		},
		[presets, state.sections, onLoadPreset]
	);

	const handleSaveNew = useCallback(() => {
		setSaveMode("create");
		setShowSaveDialog(true);
	}, []);

	const handleUpdateCurrent = useCallback(() => {
		if (!activePreset) return;
		setSaveMode("update");
		setShowSaveDialog(true);
	}, [activePreset]);

	const handleSaveSubmit = useCallback(
		(data: { description: string; includeAdditionalText: boolean; includeCoverPage: boolean; name: string; }) => {
			const snapshot = buildSnapshot(state);
			const payload = {
				name: data.name,
				description: data.description || undefined,
				settings_type: "shortlist-report",
				settings_snapshot: snapshot,
				include_cover_page: data.includeCoverPage,
				cover_page_file_id: data.includeCoverPage ? null : null,
				include_additional_text: data.includeAdditionalText,
				additional_text: data.includeAdditionalText ? state.additionalText : null,
			};

			if (saveMode === "update" && activePreset) {
				updateMutation.mutate(
					{ uuid: activePreset.uuid, payload },
					{ onSuccess: () => setShowSaveDialog(false) }
				);
			} else {
				createMutation.mutate(payload, {
					onSuccess: () => setShowSaveDialog(false),
				});
			}
		},
		[state, saveMode, activePreset, createMutation, updateMutation]
	);

	const handleDelete = useCallback(() => {
		if (!confirmDeleteUuid) return;
		deleteMutation.mutate(confirmDeleteUuid, {
			onSuccess: () => setConfirmDeleteUuid(null),
		});
	}, [confirmDeleteUuid, deleteMutation]);

	return (
		<>
			<div className="rounded-lg border border-gray-200 p-4">
				<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">
					Saved Presets
				</h3>

				{isLoading && (
					<div className="flex items-center gap-2 py-2">
						<Spinner size="xs" />
						<span className="text-sm text-gray-400">Loading presets...</span>
					</div>
				)}

				{isError && (
					<p className="text-sm text-red-500">Failed to load presets.</p>
				)}

				{!isLoading && !isError && (
					<div className="flex flex-col gap-3">
						{/* Preset selector */}
						<div>
							<select
								value={activePreset?.uuid ?? ""}
								onChange={handleLoadPreset}
								className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
							>
								<option value="">
									{presets && presets.length > 0
										? "Select a preset..."
										: "No saved presets"}
								</option>
								{presets?.map((p) => (
									<option key={p.uuid} value={p.uuid}>
										{p.name}
									</option>
								))}
							</select>
							{activePreset?.description && (
								<p className="mt-1 text-xs text-gray-500">{activePreset.description}</p>
							)}
						</div>

						{/* Action buttons */}
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={handleSaveNew}
								className="flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
							>
								<FiSave size={12} />
								Save New
							</button>
							{activePreset && (
								<>
									<button
										type="button"
										onClick={handleUpdateCurrent}
										className="flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
									>
										<FiSave size={12} />
										Update
									</button>
									<button
										type="button"
										onClick={() => setConfirmDeleteUuid(activePreset.uuid)}
										className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
									>
										<FiTrash2 size={12} />
										Delete
									</button>
								</>
							)}
						</div>
					</div>
				)}
			</div>

			{/* Save/Update dialog */}
			<SavePresetDialog
				isOpen={showSaveDialog}
				onClose={() => setShowSaveDialog(false)}
				onSave={handleSaveSubmit}
				isSaving={createMutation.isPending || updateMutation.isPending}
				mode={saveMode}
				initialValues={
					saveMode === "update" && activePreset
						? {
								name: activePreset.name,
								description: activePreset.description ?? "",
								includeCoverPage: activePreset.include_cover_page,
								includeAdditionalText: activePreset.include_additional_text,
							}
						: undefined
				}
			/>

			{/* Delete confirmation */}
			{confirmDeleteUuid && (
				<div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40">
					<div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
						<h4 className="mb-2 text-base font-semibold text-gray-800">Delete Preset?</h4>
						<p className="mb-4 text-sm text-gray-600">
							This action cannot be undone. The preset will be permanently deleted.
						</p>
						<div className="flex justify-end gap-2">
							<button
								type="button"
								onClick={() => setConfirmDeleteUuid(null)}
								className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
								disabled={deleteMutation.isPending}
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleDelete}
								className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
								disabled={deleteMutation.isPending}
							>
								{deleteMutation.isPending ? "Deleting..." : "Delete"}
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default PresetPanel;
